import { useCallback, useEffect, useMemo, useRef } from 'react';
import { walkieTargetKey, type WalkieTarget } from '@itsappy/shared';
import { useSocket } from '@/realtime/socketContext';
import { useWalkie } from '@/store/walkie';
import { startTransmitting, type WalkieTransmission } from '@/media/walkie';
import { toast } from '@/store/ui';
import { cn } from '@/lib/utils';

/**
 * Hold to talk to a whole channel — or to everyone who belongs to a space.
 *
 * Deliberately a hold and not a toggle: a radio you can leave latched open is
 * a radio someone leaves latched open. Releasing the button, moving the
 * pointer off it, or switching tabs all count as letting go.
 */
export function WalkieButton({
  target,
  targetName,
  className,
  disabled,
}: {
  target: WalkieTarget;
  /** What the button says it is talking to, in its label and tooltip. */
  targetName: string;
  /** Sizing for the row it lands in — the composer's is not a space toolbar's. */
  className?: string;
  disabled?: boolean;
}) {
  const { socket } = useSocket();
  /* Keyed on the string rather than the object: a target literal written
     inline by the caller is a new object every render, which would rebuild
     every callback below in the middle of a hold. */
  const key = walkieTargetKey(target);
  const sending = useWalkie((s) => s.sending === key);
  const held = useRef(false);
  const transmission = useRef<WalkieTransmission | null>(null);
  const sendTo = useMemo<WalkieTarget>(
    () => (target.spaceId ? { spaceId: target.spaceId } : { channelId: target.channelId }),
    [target.spaceId, target.channelId],
  );

  const release = useCallback(() => {
    if (!held.current) return;
    held.current = false;
    transmission.current?.stop();
    transmission.current = null;
    useWalkie.getState().setSending(null);
    socket?.emit('walkie:stop', sendTo);
  }, [socket, sendTo]);

  const press = useCallback(() => {
    if (held.current || disabled || !socket) return;
    held.current = true;
    useWalkie.getState().setSending(key);
    socket.emit('walkie:start', sendTo, (result) => {
      if (!held.current) return;
      if (!result.ok) {
        release();
        toast.error('Radio unavailable', result.error ?? 'Try again in a moment.');
        return;
      }
      void startTransmitting(({ seq, mimeType, data }) => {
        // Segments finish arriving slightly after the button comes up — the
        // last one is the end of the sentence, and the server still accepts it
        // because `walkie:stop` is emitted after this recorder has flushed.
        socket.emit('walkie:chunk', { ...sendTo, seq, mimeType, data });
      })
        .then((live) => {
          if (held.current) transmission.current = live;
          // Let go while the microphone was still opening: nothing to send.
          else live.stop();
        })
        .catch(() => {
          release();
          toast.error('Microphone unavailable', 'Check the browser’s microphone permission.');
        });
    });
  }, [socket, sendTo, key, disabled, release]);

  /* A held button that never gets its release — the pointer left the window,
     the tab went away — would transmit the room until the server cut it off. */
  useEffect(() => {
    if (!sending) return;
    const onHidden = () => {
      if (document.hidden) release();
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, [sending, release]);

  useEffect(() => release, [release]);

  const label = sending ? `Talking to ${targetName}` : `Hold to talk to ${targetName}`;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={sending}
      title={label}
      disabled={disabled || !socket}
      onPointerDown={(event) => {
        event.preventDefault();
        press();
      }}
      onPointerUp={release}
      onPointerCancel={release}
      /* Space and Enter are how this is held without a pointer. Keyboards
         repeat the keydown while held, which `press` ignores. */
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          press();
        }
      }}
      onKeyUp={(event) => {
        if (event.key === ' ' || event.key === 'Enter') release();
      }}
      className={cn(
        'grid shrink-0 touch-none select-none place-items-center transition-[background-color,transform] duration-150 disabled:pointer-events-none disabled:opacity-50',
        sending
          ? 'scale-95 bg-rose-600 text-white ring-4 ring-rose-500/30'
          : 'bg-surface-2 text-slate-300 ring-1 ring-surface-4 hover:bg-surface-3 hover:text-ink',
        /* Shape travels with the size: `cn` is plain clsx, so a caller's
           `rounded-lg` cannot be relied on to beat a `rounded-full` baked
           into the line above. */
        className ?? 'h-11 w-11 rounded-full',
      )}
    >
      <svg viewBox="0 0 24 24" className="h-[45%] w-[45%]" fill="none" aria-hidden>
        <rect
          x="7"
          y="9"
          width="10"
          height="12"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M14 9V5m0 0 3-2m-3 2 3 2M10 13h4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
